import { Box, FormControlLabel, Switch } from "@mui/material";
import Map, { Layer, MapMouseEvent, MapRef, Source } from "react-map-gl/mapbox";
import { featureCollection } from "@turf/turf";
import "mapbox-gl/dist/mapbox-gl.css";
import { useCallback, useMemo, useRef, useState } from "react";
import { getIncidentLayers } from "./getIncidentLayers";
import {
  createFillAppearance,
  createIncidentMarkerColour,
  createLineAppearance,
  incidentMarkerLayerId,
  interactiveLayerIds,
  styleMapper,
} from "./styles";
import IncidentInformationCard from "./information-cards/IncidentInformationCard";
import {
  createHoveredIncidentFeature,
  createSelectedIncidentCollection,
  fitFeatureBounds,
  getHoveredFeature,
  getIncidentFeatureFromMarker,
  isSameFeature,
} from "./mapFunctions";
import { MapComponentProps } from "../types";
import { useIncidentMarkers } from "./hooks/useIncidentMarkers";
import { useInteractiveFeatures } from "./hooks/useInteractiveFeatures";
import { useMapViewport } from "./hooks/useMapViewport";
import {
  useSelectedIncidentBounds,
  useFitSelectedIncidentBounds,
} from "./hooks/useIncidentBounds";
import Legend from "./information-cards/Legend";

function MapComponent({
  outline,
  bufferArea,
  selectedIncident,
  selectedIncidents,
}: MapComponentProps) {
  const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
  const mapRef = useRef<MapRef | null>(null);
  const [showIncidentMarkers, setShowIncidentMarkers] = useState(true);
  const visibleIncidentCollection = useMemo(
    () =>
      createSelectedIncidentCollection(
        selectedIncidents.length
          ? selectedIncidents
          : selectedIncident
            ? [selectedIncident]
            : [],
      ),
    [selectedIncident, selectedIncidents],
  );

  const {
    hoveredFeature,
    clickedFeature,
    hoveredFeatureCollection,
    clickedFeatureCollection,
    setHoveredFeature,
    setClickedFeature,
    clearHoveredFeature,
  } = useInteractiveFeatures();

  const { initialViewState } = useMapViewport({ outline, bufferArea });

  const selectedIncidentBounds = useSelectedIncidentBounds({
    bufferArea,
    selectedIncidentShapes: visibleIncidentCollection ?? undefined,
  });

  useFitSelectedIncidentBounds(mapRef, selectedIncidentBounds);

  const {
    incidentMarkerCollection,
    markerImageId,
    handleIncidentMarkerLoad,
    updateIncidentMarkerCollection,
  } = useIncidentMarkers(mapRef, { visibleIncidentCollection });

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      const incidentFeature = getIncidentFeatureFromMarker(
        event,
        visibleIncidentCollection,
      );

      if (incidentFeature) {
        setClickedFeature(createHoveredIncidentFeature(incidentFeature));
        fitFeatureBounds(mapRef.current, incidentFeature);
        return;
      }

      setClickedFeature(getHoveredFeature(event));
    },
    [setClickedFeature, visibleIncidentCollection],
  );

  const handleMapMouseMove = useCallback(
    (event: MapMouseEvent) => {
      const incidentFeature = getIncidentFeatureFromMarker(
        event,
        visibleIncidentCollection,
      );

      setHoveredFeature(
        incidentFeature
          ? createHoveredIncidentFeature(incidentFeature)
          : getHoveredFeature(event),
      );
    },
    [setHoveredFeature, visibleIncidentCollection],
  );

  return (
    <>
      <Box sx={{ position: "relative", width: "100%", height: 400 }}>
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 1,
            backgroundColor: "white",
            borderRadius: 1,
            px: 1,
            py: 0.5,
            boxShadow: 1,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showIncidentMarkers}
                onChange={(event) =>
                  setShowIncidentMarkers(event.target.checked)
                }
              />
            }
            label="Markers"
          />
        </Box>

        <Map
          ref={mapRef}
          data-testid="map-component"
          mapboxAccessToken={token}
          attributionControl={false}
          initialViewState={initialViewState}
          mapStyle="mapbox://styles/mapbox/light-v11"
          style={{ width: "100%", height: 400 }}
          interactiveLayerIds={interactiveLayerIds}
          onLoad={handleIncidentMarkerLoad}
          onMoveEnd={updateIncidentMarkerCollection}
          onZoomEnd={updateIncidentMarkerCollection}
          onClick={handleMapClick}
          onMouseMove={handleMapMouseMove}
          onMouseLeave={clearHoveredFeature}
        >
          {bufferArea && (
            <Source
              id="buffer"
              type="geojson"
              data={featureCollection(bufferArea)}
            >
              <Layer
                {...createFillAppearance({
                  ...styleMapper.buffer,
                  opacity: 0.08,
                })}
              />
            </Source>
          )}

          {outline && (
            <Source
              id="outline"
              type="geojson"
              data={featureCollection(outline)}
            >
              <Layer {...createFillAppearance(styleMapper.outline)} />
            </Source>
          )}

          {visibleIncidentCollection
            ? getIncidentLayers(visibleIncidentCollection)
            : null}

          {showIncidentMarkers ? (
            <Source
              id="incident-marker-source"
              type="geojson"
              data={incidentMarkerCollection}
            >
              <Layer
                id={incidentMarkerLayerId}
                type="symbol"
                layout={{
                  "icon-image": markerImageId,
                  "icon-size": 1.6,
                  "icon-anchor": "bottom",
                  "icon-allow-overlap": true,
                  "icon-offset": [0, 9],
                }}
                paint={{
                  "icon-color": createIncidentMarkerColour(),
                  "icon-halo-width": 1.5,
                }}
              />
            </Source>
          ) : null}

          {hoveredFeatureCollection ? (
            <Source
              id="hovered-incident-source"
              type="geojson"
              data={hoveredFeatureCollection}
            >
              <Layer
                {...createLineAppearance("hovered-incident-line", "#ffd600", 3)}
              />
            </Source>
          ) : null}

          {clickedFeatureCollection &&
          !isSameFeature(clickedFeature, hoveredFeature) ? (
            <Source
              id="clicked-incident-source"
              type="geojson"
              data={clickedFeatureCollection}
            >
              <Layer
                {...createLineAppearance("clicked-incident-line", "#ffd600", 3)}
              />
            </Source>
          ) : null}
        </Map>

        <IncidentInformationCard
          properties={clickedFeature.properties ?? hoveredFeature.properties}
          type={clickedFeature.type ?? hoveredFeature.type}
          apiRef={clickedFeature.apiRef ?? hoveredFeature.apiRef}
        />
      </Box>

      <Legend />
    </>
  );
}

export default MapComponent;
